import { StepFormikContainer } from './StepFormContainer.component';
import { EducationForm, getDegreeFromDegreeValue } from '../forms';
import { useMemo, useState } from 'react';
import { useProfileContext } from '../profile.context';
import { useQueryClient } from '@tanstack/react-query';
import { useL10n } from '@uplift/hooks';
import { ProfileDegreeForm } from '../forms/types';
import { saveProviderQualificationDegree } from '@uplift/api';
import { FormikConfig } from 'formik';
import { PROFILE_QUERY_KEY } from '../profile.utils';
import { degreeValidationSchema } from '../forms/validation';
import { YesNoRadioButtonGroupRaw } from '@uplift/ui-v2';
import { LocalStorageService } from '@uplift/services';

export type EducationStepProps = {
  onNext: () => void;
  onBack: () => void;
  educationFormIndex: number;
  setEducationFormIndex: (index: number) => void;
};

const EDUCATION_ADD_ANOTHER_LOCAL_STORAGE_KEY = 'education_add_another';

export const EducationStep: React.VFC<EducationStepProps> = props => {
  const l10n = useL10n();
  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  const degree = profile.qualification.degrees[props.educationFormIndex];
  const [ addAnotherDegree, setAddAnotherDegree ] = useState(() => {

    if (props.educationFormIndex === 0 || !!degree) {
      return true;
    }

    return LocalStorageService.getItem<boolean>(EDUCATION_ADD_ANOTHER_LOCAL_STORAGE_KEY);
  });

  const onSetAddAnotherDegree = (value: boolean) => {
    setAddAnotherDegree(value);
    LocalStorageService.setItem(EDUCATION_ADD_ANOTHER_LOCAL_STORAGE_KEY, value);
  };

  const onNext = () => {
    if (addAnotherDegree || props.educationFormIndex === 0) {
      props.setEducationFormIndex(props.educationFormIndex + 1);
      return;
    }

    props.onNext();
  };

  const onBack = () => {
    if (props.educationFormIndex === 0) {
      props.onBack();
      return;
    }

    props.setEducationFormIndex(props.educationFormIndex - 1);
  };

  const initialValues = useMemo((): ProfileDegreeForm => {
    return {
      id: degree?.id,
      degree: degree?.degree ?? '',
      fieldOfStudy: degree?.fieldOfStudy ?? '',
      school: degree?.school ?? '',
      yearGraduated: `${degree?.yearGraduated || ''}`,
    };
  }, [ degree ]);

  const onSubmit: FormikConfig<ProfileDegreeForm>['onSubmit'] = async (values) => {
    const { id, ...degree } = values;

    await saveProviderQualificationDegree({
      ...degree,
      replaceQualificationDegreeId: id,
      yearGraduated: Number(degree.yearGraduated),
      degree: getDegreeFromDegreeValue(degree.degree),
    });

    await queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY(profile.user.id) });
    onNext();
  };

  if (props.educationFormIndex === 0) {
    return (
      <StepFormikContainer
        title="Where did you study?"
        form={<EducationForm />}
        formikProps={{
          initialValues,
          onSubmit,
          validationSchema: degreeValidationSchema(l10n),
        }}
        onBack={onBack}
        onSkip={props.onNext}
        isSkippable={formik => !formik.dirty && !degree}
        requireValidation
      />
    );
  }

  return (
    <StepFormikContainer
      title="Do you have another degree you'd like to enter?"
      form={addAnotherDegree ? <EducationForm /> : null}
      nestForm
      formikProps={{
        initialValues,
        onSubmit,
        validationSchema: degreeValidationSchema(l10n),
      }}
      onBack={onBack}
      onSkip={onNext}
      isSkippable={() => !addAnotherDegree}
      requireValidation
    >
      <YesNoRadioButtonGroupRaw
        asBoolean
        name="addAnotherDegree"
        onClick={(addAnotherDegree: boolean) => onSetAddAnotherDegree(addAnotherDegree)}
        value={addAnotherDegree}
        disabled={degree?.id !== undefined}
      />
    </StepFormikContainer>
  );
};
